import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../reducers';
import type { PillCountsByScope, PillsMap } from '@server/pills';

export interface CanvasPillsMap extends PillsMap {
  hubspotEnrollments: PillCountsByScope;
}

export interface CanvasPillsState {
  pillsMap: {
    [crmOrgId: string]: CanvasPillsMap;
  };
  shouldPillsRecalculate: boolean;
}

const EMPTY_PILLS_MAP: CanvasPillsMap = {
  sweepAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  alerts: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  assignments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  scheduledAssignments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  sfdcAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  validationRules: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  apex: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelSfcAutomations: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelValidationRules: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  funnelApex: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  dedupe: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  matching: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
  hubspotEnrollments: {
    steps: {},
    funnels: {},
    objectType: {},
    recordType: {},
  },
};

const initialState: CanvasPillsState = {
  pillsMap: {},
  shouldPillsRecalculate: false,
};

export const canvasPillsSlice = createSlice({
  name: 'canvasPills',
  initialState,
  reducers: {
    setHubspotEnrollmentPills: (
      state,
      action: PayloadAction<{ crmOrgId: string; count: number }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      state.pillsMap[action.payload.crmOrgId].hubspotEnrollments = {
        funnels: {
          hubspot: action.payload.count,
        },
        objectType: {},
        recordType: {},
        steps: {},
      };
    },
    setShouldPillsRecalculate: (state, action: PayloadAction<boolean>) => {
      state.shouldPillsRecalculate = action.payload;
    },
    setServerPillsMap: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        pills: Omit<CanvasPillsMap, 'hubspotEnrollments'>;
      }>,
    ) => {
      if (!state.pillsMap[action.payload.crmOrgId]) {
        state.pillsMap[action.payload.crmOrgId] = { ...EMPTY_PILLS_MAP };
      }
      const target = state.pillsMap[action.payload.crmOrgId];
      const { pills } = action.payload;
      target.sfdcAutomations = pills.sfdcAutomations;
      target.validationRules = pills.validationRules;
      target.apex = pills.apex;
      target.funnelSfcAutomations = pills.funnelSfcAutomations;
      target.funnelValidationRules = pills.funnelValidationRules;
      target.funnelApex = pills.funnelApex;
      target.sweepAutomations = pills.sweepAutomations;
      target.alerts = pills.alerts;
      target.assignments = pills.assignments;
      target.dedupe = pills.dedupe;
      target.matching = pills.matching;
      target.scheduledAssignments = pills.scheduledAssignments;
    },
  },
});

export const { setHubspotEnrollmentPills, setServerPillsMap, setShouldPillsRecalculate } =
  canvasPillsSlice.actions;

export const selectShouldPillsRecalculate = (state: RootState) =>
  state.canvasPills.shouldPillsRecalculate;

export const selectPillsMap = (state: RootState) => {
  return state.canvasPills.pillsMap;
};

export const selectPillsMapByCrmOrgId =
  (crmOrgId: string) =>
  (state: RootState): CanvasPillsMap => {
    return state.canvasPills.pillsMap[crmOrgId] || EMPTY_PILLS_MAP;
  };

export default canvasPillsSlice.reducer;
